import type { C1_BaseObjectData } from "./C1_BaseObjectData";
import type { C1_ButtonProductId, C1_LightProductId } from "../data/C1_ProductDefinitions";
import type { WallSide } from "./C1_ModelTypes";
import { baseButtonDefaults } from "../../../src/models/BaseDeviceProps";

/** Room-relative placement of an independently created electrical device. */
export interface C1_InteractiveEloPlacement {
  paletteId: string;
  relativePosition: { x: number; y: number; z?: number };
  side?: WallSide;
  _wallId?: string;
  wallDocked?: "L" | "R" | "M";
  rotation: number;
  reflect?: boolean;
}

export type {
  C1_DefinitionItemData,
  C1_DefinitionRepeatDefault,
  C1_EloItem_DetailOption,
  C1_EloItem_Detail_Options,
  C1_EloItem_Definition,
  C1_EloItemPlanView,
  C1_EloItemViewAnchor,
  C1_LocalizedDefinitionText,
} from "./C1_DefinitionTypes";

export enum C1_EloItem_Type {
  Group = "group",
  PlugGroup = "plug_group",

  Blind = "blind",
  Jalousie = "jalousie",

  Light = "light",
  Plug = "plug",
  Button = "button",
  Panel = "panel",
  FixedPower = "fixed_power",
  IntermediateDistribution = "intermediate_distribution",

  DoorStation = "door_station",

  WindowContact = "window_contact",
  Sensor = "motion_sensor",

  DoorLock = "door_lock",

  Thermostat = "thermostat",
  Heating = "heating",
  Camera = "camera",
}

export { ChannelType } from "../../../src/models/ChannelType";
import { ChannelType } from "../../../src/models/ChannelType";

/** C1 names are aliases for the shared consumer channels, not an independent type system. */
export const C1_EloItem_PlugType = {
  V230Switch: ChannelType.plug_Switch,
  V230Switch1: ChannelType.plug_Switch1,
  V230Switch2: ChannelType.plug_Switch2,
  V230Switch3: ChannelType.plug_Switch3,
  V230: ChannelType.plug_230V,
  LAN: ChannelType.plug_LAN,
  LAN2: ChannelType.plug_LAN2,
  USB: ChannelType.plug_USB,
  TV: ChannelType.plug_TV,
} as const;
export type C1_EloItem_PlugType = (typeof C1_EloItem_PlugType)[keyof typeof C1_EloItem_PlugType];

export enum C1_EloItem_Voltage {
  V230 = "230V",
  V400 = "400V",
}

export enum C1_EloItem_SensorType {
  Motion = "motion",
  Temperature = "temperature",
  Humidity = "humidity",
  CO2 = "co2",
  Smoke = "smoke",
  WaterLeakDetector = "water_leak_detector",
  WindowContact = "window_contact",
}

export interface C1_EloItem_Details {
  itemCount?: number;
  numberOfButtonChannels?: number;
  buttonType?: C1_ButtonProductId;
  channelType?: ChannelType;
  isDali?: boolean;
  /** Visuelle Bauform aus `C1_ProductDefinitions.json -> lights`. */
  lightProductId?: C1_LightProductId;
  plugType?: C1_EloItem_PlugType;
  voltage?: C1_EloItem_Voltage;
  sensorTypes?: C1_EloItem_SensorType[];
}

/**
 * Sekundäre Details gehören zum vollständigen ELO-Datensatz, werden aber im
 * designorientierten Standardeditor nicht neben den primären technischen Eigenschaften
 * angezeigt.
 */
export const C1_ELO_ITEM_SECONDARY_DETAIL_KEYS = [
  "lightProductId",
] as const satisfies readonly (keyof C1_EloItem_Details)[];
export type C1_EloItem_SecondaryDetailKey = (typeof C1_ELO_ITEM_SECONDARY_DETAIL_KEYS)[number];

export function isSecondaryEloItemDetail(key: keyof C1_EloItem_Details): key is C1_EloItem_SecondaryDetailKey {
  return (C1_ELO_ITEM_SECONDARY_DETAIL_KEYS as readonly (keyof C1_EloItem_Details)[]).includes(key);
}

export type C1_ButtonChannel = Record<string, never>;

export interface C1_ButtonArea {
  shortPress?: C1_ButtonChannel;
  longPress?: C1_ButtonChannel;
  veryLongPress?: C1_ButtonChannel;
}

export type C1_ButtonRowButtons = [C1_ButtonArea] | [C1_ButtonArea, C1_ButtonArea];

export interface C1_ButtonRow {
  /** Eine gemeinsame oder zwei gesplittete Bedienflächen. */
  buttons: C1_ButtonRowButtons;
  /** Zusätzliche Bedienfläche mit denselben Druckarten wie ein Button. */
  slap: C1_ButtonArea;
}

export interface C1_ButtonProperties {
  type: C1_EloItem_Type.Button;
  widthMm: number;
  heightMm: number;
  color: C1_ButtonColor;
  orientation: C1_ButtonOrientation;
  hasDisplay: boolean;
  supportsSlap: 0 | 1 | 2;
  numberOfButtonRows: number;
  /** @deprecated Individual button configuration lives on ESTADA Button.rockerConfigs. */
  rows?: C1_ButtonRow[];
}

export type C1_ButtonColor = string;
export type C1_ButtonOrientation = "horizontal" | "vertical";

export type C1_ButtonDefaults = Pick<
  C1_ButtonProperties,
  "numberOfButtonRows" | "color" | "orientation" | "hasDisplay" | "supportsSlap"
>;

export type C1_ButtonDefaultsByType = Record<"simple" | "touch", C1_ButtonDefaults>;

// Compatibility projection for equipment creation; all defaults come from the shared product model.
function buttonDefaultsForC1(type: "Simple" | "Touch"): C1_ButtonDefaults {
  const defaults = baseButtonDefaults(type);
  return {
    numberOfButtonRows: defaults.numberOfButtonRows,
    color: defaults.color || "white",
    orientation: defaults.horizontal ? "horizontal" : "vertical",
    hasDisplay: defaults.hasDisplay,
    supportsSlap: defaults.supportsSlap,
  };
}

export const C1_DEFAULT_BUTTON_DEFAULTS: C1_ButtonDefaultsByType = {
  simple: buttonDefaultsForC1("Simple"),
  touch: buttonDefaultsForC1("Touch"),
};

/** Typsichere Namen aller persistierten Taster-Properties. */
export const C1_BUTTON_PROPERTY_NAMES = {
  type: "type",
  widthMm: "widthMm",
  heightMm: "heightMm",
  color: "color",
  orientation: "orientation",
  hasDisplay: "hasDisplay",
  supportsSlap: "supportsSlap",
  numberOfButtonRows: "numberOfButtonRows",
  rows: "rows",
} as const satisfies { [Key in keyof C1_ButtonProperties]: Key };

export type C1_ButtonPropertyName = keyof C1_ButtonProperties;

/** Wird mit weiteren typabhängigen Property-Strukturen erweitert. */
export type C1_EloItem_Properties = C1_ButtonProperties;

export function createDefaultButtonProperties(
  defaults?: {
    buttonRows: number;
    width: number;
    height: number;
    uiTemplate?: string;
  },
  // Kept for legacy equipment factories; project-specific button defaults are no longer applied.
  _preferences?: Partial<C1_ButtonDefaults>,
): C1_ButtonProperties {
  const preferences = defaults?.uiTemplate === "panel" ? undefined
    : C1_DEFAULT_BUTTON_DEFAULTS[defaults?.uiTemplate === "touch" ? "touch" : "simple"];
  const numberOfButtonRows = Math.max(1, Math.floor(preferences?.numberOfButtonRows ?? defaults?.buttonRows ?? 2));
  return {
    type: C1_EloItem_Type.Button,
    widthMm: defaults?.width ?? 70,
    heightMm: defaults?.height ?? 70,
    color: preferences?.color ?? "white",
    orientation: preferences?.orientation ?? "vertical",
    hasDisplay: preferences?.hasDisplay ?? false,
    supportsSlap: preferences?.supportsSlap ?? 0,
    numberOfButtonRows,
  };
}

/**
 * Persisted values of an ELO facet attached to an ESTADA model object.
 *
 * The facet has no identity and no child collection of its own. Its identity
 * and hierarchy are supplied by the owning ESTADA Group, Consumer or Button.
 * `_defId` is the only link to the expanded, non-persistent catalog model.
 */
export interface C1_EloItemInstanceData extends C1_BaseObjectData {
  /** Array geometry only. Room placement belongs exclusively to interactivePlacement. */
  lightGroupFurniture?: { baseProps: { width: number; height: number; count?: number; rows?: number } };
  interactivCreated?: boolean;
  interactivePlacement?: C1_InteractiveEloPlacement;
  /** Structural legacy fields are forbidden on facet payloads. */
  id?: never;
  definitionId?: never;
  items?: never;
  name?: never;
  tag?: never;
  _defId?: string;
  /** Optionale manuelle Position in Metern relativ zum Ursprung des besitzenden Objekts. */
  position?: { x: number; y: number; z?: number };
  count?: number;
  /** One-based occurrence of an instance repeated from the same definition. */
  repeatIndex?: number;
  details?: C1_EloItem_Details;
  properties?: C1_EloItem_Properties;
}

/**
 * Legacy serialized ELO tree used by editors and project migration code.
 *
 * New C1 facets must not use this type. `id`, `definitionId` and `items` are
 * artifacts from the former parallel C1 object tree and will disappear once
 * the remaining import/editor adapters have been migrated to ESTADA objects.
 */
export interface C1_LegacyEloItemTreeData extends C1_BaseObjectData {
  interactivCreated?: boolean;
  interactivePlacement?: C1_InteractiveEloPlacement;
  id: string;
  definitionId: string;
  name?: string;
  tag?: string;
  position?: { x: number; y: number; z?: number };
  count?: number;
  details?: C1_EloItem_Details;
  properties?: C1_EloItem_Properties;
  items: C1_LegacyEloItemTreeData[];
}

/** @deprecated Use `C1_EloItemInstanceData` for facets. Kept for legacy tree adapters only. */
export type C1_EloItem_Data = C1_LegacyEloItemTreeData;
